import React, { useState } from "react";
import { Copy, Check, ChevronDown, ChevronUp } from "lucide-react";
import CodeBlock from "./CodeBlock";

const METHOD_STYLES = {
  GET: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  POST: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  PUT: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  DELETE: "bg-red-500/15 text-red-400 border-red-500/30",
};

const JsonBlock = ({ data, label }) => {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(data, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
        <button onClick={handleCopy} className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors" title="Copy">
          {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
        </button>
      </div>
      <pre className="bg-[#0d1117] border border-gray-800 rounded-lg p-4 overflow-x-auto text-[13px] leading-relaxed text-white">
        {json}
      </pre>
    </div>
  );
};

const FieldsTable = ({ fields, label }) => {
  if (!fields || fields.length === 0) return null;
  return (
    <div>
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">{label}</span>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2 font-semibold text-gray-600">Field</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-600">Type</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-600">Required</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-600">Description</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => (
              <tr key={f.name} className="border-b border-gray-100 last:border-0">
                <td className="px-3 py-2 font-mono text-[#048AAD]">{f.name}</td>
                <td className="px-3 py-2 text-gray-500">{f.type}</td>
                <td className="px-3 py-2">
                  {f.required ? (
                    <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-red-50 text-red-500">required</span>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">optional</span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-600">{f.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EndpointDoc = ({ endpoint }) => {
  const [showHeaders, setShowHeaders] = useState(false);

  if (!endpoint) return null;

  return (
    <div id={endpoint.id} className="scroll-mt-6">
      <div className="flex items-center gap-3 mb-2">
        <span className={`px-2.5 py-1 rounded-md text-[11px] font-bold uppercase border ${METHOD_STYLES[endpoint.method] || ""}`}>
          {endpoint.method}
        </span>
        <code className="text-sm font-mono text-gray-700 bg-gray-100 px-3 py-1 rounded-md">{endpoint.path}</code>
      </div>

      <h2 className="text-lg font-bold text-gray-900 mb-1">{endpoint.title}</h2>
      <p className="text-sm text-gray-500 mb-5 leading-relaxed">{endpoint.description}</p>

      {endpoint.headers && (
        <div className="mb-4">
          <button
            onClick={() => setShowHeaders(!showHeaders)}
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors"
          >
            Headers
            {showHeaders ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {showHeaders && (
            <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
              {Object.entries(endpoint.headers).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-xs font-mono py-0.5">
                  <span className="text-gray-500">{k}:</span>
                  <span className="text-gray-700">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="space-y-5">
        <FieldsTable fields={endpoint.requestFields} label="Request Body Parameters" />
        <FieldsTable fields={endpoint.queryParams} label="Query Parameters" />

        {endpoint.requestBody && <JsonBlock data={endpoint.requestBody} label="Example Request Body" />}
        {endpoint.responseBody && <JsonBlock data={endpoint.responseBody} label="Example Response" />}

        {endpoint.codeExamples && (
          <div>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Code Examples</span>
            <CodeBlock codeExamples={endpoint.codeExamples} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EndpointDoc;
